// src/AGTUps-Product/components/AGTCashFlow-products/DuesPayments.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Chip,
  Avatar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Collapse,
  Alert,
  Snackbar,
  useMediaQuery,
  Menu,
  Tooltip
} from '@mui/material';
import { styled, useTheme} from '@mui/material/styles';
import {
  Calendar,
  RepeatIcon,
  Receipt,
  DollarSign,
  MoreVertical,
  Trash2,
  Edit2,
  Flag,
  Plus,
  Lock
} from 'lucide-react';
import PaymentService from '../../services/AGTUps-product-services/paymentService';
import PaymentsSetupWizard from './Design-DuesPayments/PaymentsSetupWizard';

// =================== ESTILOS ===================

const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000',
    },
    bg: { primaryElevated: '#FFFFFF' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' },
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000',
    },
    bg: { primaryElevated: '#000000' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' },
  },
};

const PaymentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: 'calc(100vh - 180px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  },
}));

const StyledPaperUnified = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: 'none',
  };
});

const SchedulePaymentButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    borderRadius: '30px',
    textTransform: 'none',
    gap: theme.spacing(1),
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.9)' : '#0266cc',
      boxShadow: 'none',
    },
  };
});

const StyledSelect = styled(Select)(({ theme }) => {
  const mode = theme.palette.mode;
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : (mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    borderRadius: '12px',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${
        mode === 'dark'
          ? appleColors.dark.system.grey3
          : appleColors.light.system.grey3
      }`,
    '& .MuiSelect-select': {
      padding: '12px 16px',
      fontSize: '0.95rem',
      fontWeight: 500,
    },
    '& fieldset': {
      border: 'none',
    },
  };
});

const StyledTextField = styled(TextField)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.25)'
        : (theme.palette.mode === 'dark'
          ? appleColors.dark.ui.base
          : appleColors.light.ui.base),
      backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
      '& fieldset': {
        border: 'none',
      },
    },
  };
});

// =================== DATOS ESTÁTICOS ===================

const categories = [
  { id: 'software', name: 'Software & Services', color: '#4f46e5' },
  { id: 'rent', name: 'Rent & Lease', color: '#0891b2' },
  { id: 'utilities', name: 'Utilities', color: '#059669' },
  { id: 'supplies', name: 'Office Supplies', color: '#7c3aed' },
];

const statusTabs = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Completed', value: 'completed' },
];

// Función para formatear la descripción (si es objeto, la convierte a string)
const formatDescription = (desc) => {
  if (!desc) return '';
  if (typeof desc === 'object') {
    // Puedes personalizar el formateo; por ejemplo, mostrar solo invoice_number y payment_method
    const { invoice_number, payment_method } = desc;
    return `Invoice: ${invoice_number || 'N/A'} | Method: ${payment_method || 'N/A'}`;
    // O simplemente: return JSON.stringify(desc);
  }
  return desc;
};

// =================== COMPONENTE PRINCIPAL: UpcomingPayments ===================

const UpcomingPayments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [openScheduleWizard, setOpenScheduleWizard] = useState(false);
  const [openEditWizard, setOpenEditWizard] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [statusTab, setStatusTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const handlePaymentCreated = (newPayment) => {
    setPayments((prev) => [newPayment, ...prev]);
    setSnackbar({ open: true, message: 'Payment created successfully!' });
  };

  const handlePaymentUpdated = (updatedPayment) => {
    setPayments((prev) =>
      prev.map((p) =>
        p.paymentId === updatedPayment.paymentId ? updatedPayment : p
      )
    );
    setSnackbar({ open: true, message: 'Payment updated successfully!' });
  };

  const handlePaymentDeleted = (paymentId) => {
    setPayments((prev) => prev.filter((p) => p.paymentId !== paymentId));
    setSnackbar({ open: true, message: 'Payment deleted successfully!' });
  };

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const response = await PaymentService.getPayments();
        if (response.success) {
          setPayments(response.data);
        }
      } catch (error) {
        console.error('Error fetching payments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, []);

  const filteredPayments = useMemo(() => {
    return payments.filter((payment) => {
      const matchesCategory =
        filterCategory === 'all' || payment.category === filterCategory;
      const matchesStatus =
        statusTab === 'all' || payment.status === statusTab;
      const matchesSearch = (payment.title || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesCategory && matchesStatus && matchesSearch;
    });
  }, [payments, filterCategory, statusTab, searchTerm]);

  const getTotalUpcoming = () => {
    return payments.reduce((total, payment) => {
      if (payment.daysUntilDue === 0 && !payment.automaticPayment) {
        return total;
      }
      return total + Number(payment.amount);
    }, 0);
  };

  const getDueDateColor = (daysUntilDue) => {
    if (daysUntilDue <= 3) return 'error';
    if (daysUntilDue <= 7) return 'warning';
    return 'success';
  };

  const handleOpenMenu = (event, payment) => {
    event.stopPropagation();
    setSelectedPayment(payment);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = (payment) => {
    setSelectedPayment(payment);
    setOpenEditWizard(true);
    handleCloseMenu();
  };

  const handleDelete = (payment) => {
    setSelectedPayment(payment);
    setOpenDeleteDialog(true);
    handleCloseMenu();
  };

  return (
    <PaymentContainer className="space-y-6">
      {/* Fila 1: Input de búsqueda por título */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <StyledTextField
            fullWidth
            label="Search by Title"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
          />
        </Grid>
      </Grid>

      {/* Fila 2: Selector de categorías */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item>
          <FormControl fullWidth={isMobile ? true : false}>
            <InputLabel>Category</InputLabel>
            <StyledSelect
              value={filterCategory}
              label="Category"
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Grid>
      </Grid>

      {/* Fila 3: Pestañas/selector de estado y botón Schedule Payment */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 2,
        }}
      >
        {isMobile ? (
          <FormControl sx={{ flex: 1, mr: 2 }}>
            <InputLabel>Status</InputLabel>
            <StyledSelect
              label="Status"
              value={statusTab}
              onChange={(e) => setStatusTab(e.target.value)}
            >
              {statusTabs.map((tab) => (
                <MenuItem key={tab.value} value={tab.value}>
                  {tab.label}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        ) : (
          <Tabs
            value={statusTab}
            onChange={(e, newValue) => setStatusTab(newValue)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              '& .MuiTab-root': {
                color: theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.7)' : 'inherit',
                '&.Mui-selected': {
                  color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main,
              },
            }}
          >
            {statusTabs.map((tab) => (
              <Tab
                key={tab.value}
                label={tab.label}
                value={tab.value}
                sx={{ textTransform: 'none' }}
              />
            ))}
          </Tabs>
        )}
        <SchedulePaymentButton
          onClick={() => setOpenScheduleWizard(true)}
          startIcon={<Plus />}
        >
          Schedule Payment
        </SchedulePaymentButton>
      </Box>

      {/* Tarjetas resumen */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={4}>
          <StyledPaperUnified>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <DollarSign
                size={20}
                style={{
                  color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total Due
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (Excludes overdue manual confirmation)
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              ${getTotalUpcoming().toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Next 30 days
            </Typography>
          </StyledPaperUnified>
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledPaperUnified>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Calendar size={20} style={{ color: '#2DD257' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Due This Week
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (Next 7 days)
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {filteredPayments.filter((p) => p.daysUntilDue <= 7).length} Payments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total $
              {filteredPayments
                .filter((p) => p.daysUntilDue <= 7)
                .reduce((sum, p) => sum + Number(p.amount), 0)
                .toLocaleString()}
            </Typography>
          </StyledPaperUnified>
        </Grid>
        <Grid item xs={12} md={4}>
          <StyledPaperUnified>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RepeatIcon size={20} style={{ color: '#7c3aed' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Recurring
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (Active recurring)
              </Typography>
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {filteredPayments.filter((p) => p.type === 'recurring').length} Active
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monthly payments
            </Typography>
          </StyledPaperUnified>
        </Grid>
      </Grid>

      {/* Lista de pagos */}
      <StyledPaperUnified sx={{ mt: 3 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }} gutterBottom>
            Upcoming Payments
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredPayments.length} payments
          </Typography>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredPayments.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography variant="h6" color="text.secondary">
              No payments found.
            </Typography>
          </Box>
        ) : (
          filteredPayments.map((payment) => (
            <StyledPaperUnified key={payment.paymentId} sx={{ mb: 2, p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor:
                        categories.find((cat) => cat.id === payment.category)
                          ?.color || '#000',
                      width: 40,
                      height: 40,
                    }}
                  >
                    <Receipt size={20} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {payment.title ||
                        (typeof payment.description === 'object'
                          ? formatDescription(payment.description)
                          : payment.description)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(payment.dueDate).toLocaleDateString()}
                    </Typography>
                    {/* Chip de estado: si está completed, se muestra en verde */}
                    <Chip
                      size="small"
                      label={payment.status}
                      color={
                        payment.status === 'completed'
                          ? 'success'
                          : payment.status === 'scheduled'
                          ? 'warning'
                          : 'default'
                      }
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    ${Number(payment.amount).toLocaleString()}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => handleOpenMenu(e, payment)}
                    sx={{
                      color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit',
                      '&:hover': {
                        backgroundColor: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <MoreVertical size={16} />
                  </IconButton>
                </Box>
              </Box>
              <Collapse in timeout="auto">
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {typeof payment.description === 'object'
                      ? formatDescription(payment.description)
                      : payment.description}
                  </Typography>
                  {/* Si el pago no está completado, se muestra el Chip con Due in X days */}
                  {payment.status !== 'completed' && (
                    payment.daysUntilDue === 0 && !payment.automaticPayment ? (
                      <Chip
                        size="small"
                        label="Manual Confirmation Needed"
                        color="error"
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <Chip
                        size="small"
                        label={`Due in ${payment.daysUntilDue} days`}
                        color={getDueDateColor(payment.daysUntilDue)}
                        sx={{ mt: 1 }}
                      />
                    )
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    Payee: {payment.payee}
                  </Typography>
                </Box>
              </Collapse>
            </StyledPaperUnified>
          ))
        )}
      </StyledPaperUnified>

      {/* Menú de acción */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 4px 8px rgba(0, 0, 0, 0.5)'
                : '0 4px 8px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedPayment && !selectedPayment.isCost) {
              handleEdit(selectedPayment);
            }
          }}
          disabled={selectedPayment && selectedPayment.isCost}
        >
          {selectedPayment && selectedPayment.isCost ? (
            <Tooltip title="This payment comes from the Cost module. Edit it there.">
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <Lock size={16} style={{ marginRight: 8 }} />
                Edit Payment
              </span>
            </Tooltip>
          ) : (
            <>
              <Edit2 size={16} style={{ marginRight: 8 }} />
              Edit Payment
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedPayment && !selectedPayment.isCost) {
              handleDelete(selectedPayment);
            }
          }}
          disabled={selectedPayment && selectedPayment.isCost}
          sx={{ color: 'error.main' }}
        >
          {selectedPayment && selectedPayment.isCost ? (
            <Tooltip title="This payment comes from the Cost module. Delete it there.">
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <Lock size={16} style={{ marginRight: 8 }} />
                Delete Payment
              </span>
            </Tooltip>
          ) : (
            <>
              <Trash2 size={16} style={{ marginRight: 8 }} />
              Delete Payment
            </>
          )}
        </MenuItem>
        <MenuItem>
          <Flag size={16} style={{ marginRight: 8 }} />
          Mark as Pending
        </MenuItem>
      </Menu>

      {/* Wizard para crear un pago */}
      <PaymentsSetupWizard
        open={openScheduleWizard}
        onClose={() => setOpenScheduleWizard(false)}
        onSubmit={async (paymentData) => {
          const response = await PaymentService.createPayment(paymentData);
          if (response.success) {
            handlePaymentCreated(response.data);
            setOpenScheduleWizard(false);
          }
        }}
        mode="create"
      />

      {/* Wizard para editar un pago */}
      <PaymentsSetupWizard
        open={openEditWizard}
        onClose={() => setOpenEditWizard(false)}
        initialData={selectedPayment}
        onSubmit={async (paymentData) => {
          const response = await PaymentService.updatePayment(
            selectedPayment.paymentId,
            paymentData
          );
          if (response.success) {
            handlePaymentUpdated(response.data);
            setOpenEditWizard(false);
          }
        }}
        mode="edit"
      />

      {/* Diálogo de eliminación */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor:
              theme.palette.mode === 'light'
                ? 'rgba(0, 0, 0, 0.12)'
                : 'rgba(255, 255, 255, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Delete Payment
          </Typography>
          <IconButton size="small" onClick={() => setOpenDeleteDialog(false)}>
            <Trash2 size={18} color={theme.palette.error.main} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
            This action cannot be undone.
          </Alert>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to delete this payment?
            </Typography>
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'rgba(0, 0, 0, 0.02)',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">
                    {selectedPayment?.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {typeof selectedPayment?.description === 'object'
                      ? formatDescription(selectedPayment.description)
                      : selectedPayment?.description}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Amount: ${selectedPayment?.amount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Due: {new Date(selectedPayment?.dueDate).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: #{selectedPayment?.paymentId}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            sx={{
              textTransform: 'none',
              color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF'
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setLoading(true);
              try {
                const response = await PaymentService.deletePayment(
                  selectedPayment.paymentId
                );
                if (response.success) {
                  handlePaymentDeleted(selectedPayment.paymentId);
                  setOpenDeleteDialog(false);
                }
              } catch (error) {
                console.error('Error deleting payment:', error);
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            color="error"
            sx={{
              textTransform: 'none',
              borderRadius: '12px',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Delete Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones de éxito */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </PaymentContainer>
  );
};

export default UpcomingPayments;
